<template>
  <v-card flat tile class="fid-card" v-if="usePlayer && itemReason">
    <v-layout row wrap fill-height xs12>
      <v-flex xs12>
        <!-- put the slider above all the detail and apply to all -->
        <v-card-actions>
          <v-slider
            v-model="viSliderModel"
            thumb-label="always"
            :thumb-size="20"
            :prepend-icon="viSliderIcon"
          ></v-slider>
        </v-card-actions>

        <!-- first the poster frame images -->
        <v-card-text class="pb-0 pt-0" v-if="presentable('postersection')">
          <poster-slides
            :reason="itemReason"
            :vimeo-pictures="VimeoVideoPicturesInfo"
            :editable="editable"
            :master="master"
          ></poster-slides>
        </v-card-text>

        <!-- now the vilabels -->
        <v-card-text class="pt-0 pb-0" v-if="presentable('labelsection')">
          <!-- Vi label section header showing progress etc-->

          <v-card-title>
            <span class="subtitle-1">
              <span v-if="progressRunning"
                >AI cloud labelling under way - check back later</span
              >

              <span v-else-if="!someViLabels"
                >Content analysis hasn't been run yet on this film</span
              >
              <span v-else>Content analysis</span>
            </span>
            <v-spacer></v-spacer>

            <!-- rerunnable icon-->
            <v-tooltip
              v-if="showRerunIcon"
              left
              color="secondary"
              content-class="caption"
              :close-delay="tooltips.closeDelay"
              :open-on-hover="true"
              :transition="tooltips.transition"
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  v-on="on"
                  small
                  icon
                  @click="generateViLabels"
                  :disabled="!progressRunnable"
                >
                  <v-icon small>{{ getIcon('rerun') }}</v-icon>
                </v-btn>
              </template>
              <span>Redo video labelling</span>
            </v-tooltip>

            <!-- Number of vi label items found-->
            <tool-chip-count
              v-if="someViLabels && !progressRunning"
              :count="someViLabels"
              class="mr-1"
              :tooltip="`${someViLabels} items analyzed`"
              left
            ></tool-chip-count>

            <!-- if there's none, then show the progress icon-->
            <span>
              <v-tooltip
                v-if="progressIcon && !someViLabels"
                right
                :color="progressTooltip.color"
                content-class="caption"
                :close-delay="tooltips.closeDelay"
                :open-on-hover="true"
                :transition="tooltips.transition"
              >
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on" small :color="progressIconColor">{{
                    progressIcon
                  }}</v-icon>
                </template>
                <span v-if="progressTooltip.tip">{{
                  progressTooltip.tip
                }}</span>
              </v-tooltip>

              <v-btn
                small
                v-if="showGenerateButton"
                :disabled="!progressRunnable || !allowGenerate"
                color="primary"
                @click="generateViLabels"
                >Generate</v-btn
              >
            </span>

            <!-- vi label section detail flipper-->
            <hide-detail
              v-if="someViLabels"
              prop-name="labelsection"
              tooltip="analyzed items"
              left
              :hide="!showLabels"
              @hide="hideSection"
            ></hide-detail>
          </v-card-title>
          <vi-labels
            v-if="showLabels && someViLabels"
            :reason="itemReason"
            :editable="editable"
            @refreshfilm="refreshFilm"
            :dirty="dirty"
            :master="master"
          ></vi-labels>
        </v-card-text>

        <!-- similar content -->
        <v-card-text
          class="pt-0 pb-0"
          v-if="presentable('similarsection') && someViLabels"
        >
          <v-card-title>
            <span class="subtitle-1">Similar content analysis</span>
            <v-spacer></v-spacer>
            <!-- Number of vi label items found-->
            <tool-chip-count
              v-if="countByType('object-label')"
              :count="countByType('object-label')"
              class="mr-1"
              :tooltip="`${countByType(
                'object-label',
              )} tracking items analyzed`"
              left
            ></tool-chip-count>
            <hide-detail
              prop-name="similarsection"
              tooltip="similar content detection"
              left
              :hide="!showSimilar"
              @hide="hideSection"
            ></hide-detail>
          </v-card-title>
          <similar-content
            label-type="object-label"
            label-caption="Similar content detection objects"
            :fmid="filmMasterID"
            :film="film"
            :count="countByType('object-label')"
            @refreshfilm="refreshFilm"
            v-if="showSimilar"
          ></similar-content>
        </v-card-text>

        <!-- color strips -->
        <v-card-text
          class="pt-0 pb-0"
          v-if="presentable('stripsection') && masterStrip"
        >
          <v-card-title>
            <span class="subtitle-1">Color Strip Analysis</span>
            <v-spacer></v-spacer>
            <!-- instead of a chip count we use the color strip itself -->
            <v-img :src="masterStrip" :max-width="stripWidth"></v-img>

            <!-- strip search flipper -->
            <hide-detail
              prop-name="stripsection"
              tooltip="color strip matching"
              left
              :hide="!showStrip"
              @hide="hideSection"
            ></hide-detail>
          </v-card-title>
          <color-analysis
            :master="master"
            :reason="itemReason"
            v-if="showStrip"
          >
          </color-analysis>
          <vi-labels
            :reason="itemReason"
            :editable="editable"
            similarsection
            @refreshfilm="refreshFilm"
            :dirty="dirty"
            :master="master"
          ></vi-labels>
        </v-card-text>

        <!-- call sheets should have an admin test TODO -->
        <v-card-text
          class="pt-0 pb-0"
          v-if="presentable('callsheetsection') && permittedEditor"
        >
          <callsheets
            :reason="itemReason"
            :editable="editable"
            :master="master"
          ></callsheets>
        </v-card-text>
      </v-flex>
    </v-layout>
  </v-card>
</template>
<script>
import { showToaster, mutate } from '@/js/apollo'
import Similarcontent from '@/components/Similarcontent'
import { getIconColor, getIcon, settings, isBoss } from '@/js/store'

import Posterslides from '@/components/Posterslides'
import Vilabels from '@/components/Vilabels'
import Callsheets from '@/components/Callsheets'
import Coloranalysis from '@/components/Coloranalysis'
import { SearchUtils } from '@/js'
import {
  VimeoVideoPicturesInfo,
  ProviderAuth,
  MasterTypes,
  provokeViLabel,
  ViLabelProgress,
} from '@/gql'
import { mapState, mapMutations, mapGetters } from 'vuex'
import { vxMaps } from '@/js/vxstore'
import Hidedetail from '@/components/Hidedetail'
import Toolchipcount from '@/components/Toolchipcount'

export default {
  props: {
    itemReason: Object,
    usePlayer: Boolean,
    rowType: String,
    allPrizes: Array,
    storyHeader: String,
    dirty: Boolean,
    master: Object,
  },
  components: {
    'poster-slides': Posterslides,
    'vi-labels': Vilabels,
    Callsheets,
    'color-analysis': Coloranalysis,
    'similar-content': Similarcontent,
    'hide-detail': Hidedetail,
    'tool-chip-count': Toolchipcount,
  },
  data: () => ({
    VimeoVideoPicturesInfo: null,
    ProviderAuth: null,
    activeSection: null,
    MasterTypes: null,
    ViLabelProgress: null,
    polling: false,
    startingToPoll: false,
    pollInterval: 0,
    masterDirty: false,
    allowGenerate: true,
  }),
  watch: {
    progress() {
      this.setPollInterval()
    },
    filmMasterID() {
      // when this changes we dont want to be stuck on a sub menu
      this.setVActiveSub(null)
    },
  },
  methods: {
    getIcon(name) {
      return getIcon(name)
    },
    getIconColor(name) {
      return getIconColor(name)
    },
    toastLabellingError() {
      showToaster({
        header: 'video labelling failure',
        type: 'error',
        message: 'failed to do labelling - try again',
        error: this.labellingError,
      })
    },
    provoker({ gcsUrl, loiter = 10 }) {
      return mutate({
        mutation: provokeViLabel,
        fetchPolicy: 'no-cache',
        variables: {
          input: {
            filmMasterID: this.filmMasterID,
            gcsUrl,
          },
        },
      })
        .then((result) => {
          // wait a bit before starting pilrting to
          return SearchUtils.loiter(loiter).then(() => {
            showToaster({
              header: 'Video labelling being processing in the cloud',
              type: 'message',
              message:
                'thank you for waiting - please check back later for results',
            })
            // start polling
            this.setPollInterval(true)
            return result
          })
        })
        .catch((err) => {
          showToaster({
            header: 'video labelling provokefailure',
            type: 'error',
            message: 'failed to do labelling - try again',
            error: err,
          })
        })
    },
    generateViLabels() {
      this.allowGenerate = false
      // because this will streamed directly in the cloud
      return this.provoker({ loiter: 100, gcsUrl: null })
    },
    setPollInterval(start) {
      const val = this.progress
      this.startingToPoll = start

      // keeps polling on progress till its done or an error or a timeout
      if (
        this.startingToPoll ||
        (val && val.status !== 'done' && !val.error && !this.progressExpired)
      ) {
        // make the first one a bit quicker
        const pollInterval = this.startingToPoll ? 3000 : 10000
        // possibly need to restart the polling with a different interval
        if (this.pollInterval !== pollInterval) {
          this.$apollo.queries.ViLabelProgress.stopPolling()
          this.pollInterval = 0
        }
        if (!this.pollInterval) {
          this.$apollo.queries.ViLabelProgress.startPolling(pollInterval)
        }
        this.pollInterval = pollInterval
      } else {
        this.pollInterval = 0
        this.$apollo.queries.ViLabelProgress.stopPolling()
      }
      // do a refetch on the labels to get whatever has changed
      this.$apollo.queries.MasterTypes.refetch()
      this.masterDirty = true
    },
    hideSection(pack) {
      this.setBigActiveSection(pack.hide ? null : pack.propName)
    },
    countByType(t) {
      return this.countsByType && this.countsByType[t]
    },
    getStripUrl(master) {
      return (
        master && master.StripArtifact && master.StripArtifact.SignedUrl.url
      )
    },
    refreshFilm(film) {
      this.$emit('refreshfilm', film)
    },
    // section is showable if none are showable, or its selected
    presentable(propName) {
      const isPresentable =
        !this.bigActiveSection || this.bigActiveSection === propName
      return isPresentable
    },
    ...mapMutations(vxMaps.mutations),
  },

  computed: {
    tooltips() {
      return settings.tooltips
    },
    showRerunIcon() {
      return (
        !this.showGenerateButton &&
        this.editable &&
        (this.progress || this.progressExpired || this.someViLabels)
      )
    },
    showGenerateButton() {
      return !this.progress && this.editable && !this.someViLabels
    },
    progress() {
      return this.ViLabelProgress
    },
    errorIcon() {
      return getIcon('error')
    },
    progressError() {
      return this.progress && this.progress.error
    },
    progressSuccess() {
      return this.progress && this.progress.success
    },
    progressRunning() {
      return (
        this.progress &&
        !this.progressError &&
        !this.progressSuccess &&
        !this.progressExpired
      )
    },
    progressRunnable() {
      return !this.progressRunning
    },
    progressIconItem() {
      if (!this.progress) return null

      if (this.startingToPoll) {
        return 'waiting'
      } else if (this.progressError) {
        return 'error'
      } else if (this.progressSuccess) {
        return this.progress.status
      } else if (this.progressExpired) {
        return 'expired'
      } else {
        return this.progress.status
      }
    },
    progressIcon() {
      const pitem = this.progressIconItem
      return pitem ? getIcon(pitem) : null
    },
    progressIconColor() {
      const pitem = this.progressIconItem
      return pitem ? getIconColor(pitem) : null
    },
    progressTooltip() {
      if (!this.progress)
        return {
          color: 'primary',
          tip: null,
        }

      if (this.startingToPoll)
        return {
          color: 'accent',
          tip: 'Waiting to analyze',
        }

      if (this.progressError)
        return {
          color: 'error',
          tip: this.progress.error,
        }

      if (this.progressExpired)
        return {
          color: 'error',
          tip: 'Expired without finishing - try again',
        }
      return {
        color: 'secondary',
        tip: this.progress.status,
      }
    },
    progressExpired() {
      return (
        this.progress &&
        !this.progressSuccess &&
        !this.progressError &&
        SearchUtils.progressExpired({
          runID: this.progress.runID,
          timeout: settings.viLabels.analysisProgress.timeout,
        })
      )
    },
    labellingError() {
      return this.progress && this.progress.error
    },
    isProgressRequired() {
      return this.filmMasterID && !this.someViLabels && this.editable
    },
    countsByAll() {
      return this.countsByType
        ? Object.keys(this.countsByType).reduce(
            (p, c) => p + this.countsByType[c],
            0,
          )
        : 0
    },
    someViLabels() {
      const s = this.countsByType && this.countsByAll
      return s
    },
    showStrip() {
      return this.bigActiveSection === 'stripsection'
    },
    showLabels() {
      return this.bigActiveSection === 'labelsection'
    },
    showSimilar() {
      return this.bigActiveSection === 'similarsection'
    },
    countsByType() {
      const data =
        this.MasterTypes && this.MasterTypes[0] && this.MasterTypes[0].ViLabels
      if (!data) return null
      // summarize that by label type
      return data.reduce((p, c) => {
        p[c.CountViGroup.type] = c.CountViGroup.countRows
        return p
      }, {})
    },
    film() {
      return this.itemReason && this.itemReason.Film
    },
    filmMasterID() {
      return this.master && this.master.id
    },
    viSliderModel: {
      get() {
        return this.viSlider
      },
      set(value) {
        this.setViSlider(value)
      },
    },
    viSliderIcon() {
      return getIcon('confidence')
    },
    stripWidth() {
      return 220
    },
    masterStrip() {
      const strip = this.getStripUrl(this.master)
      return strip
    },

    hostID() {
      return this.master && this.master.hostID
    },
    hostType() {
      return this.master && this.master.hostType
    },
    permittedEditor() {
      // this is temporary till we figure out how to validate ownership of films
      return isBoss(this.ProviderAuth && this.ProviderAuth.userID)
    },
    // whether this page is editable, and for what reason
    editable() {
      // for testing turn on editing for bosses anyway

      const editable = Boolean(this.permittedEditor ? 'fid' : '')

      return editable
    },
    breakpoint() {
      return this.$vuetify.breakpoint
    },
    userID() {
      return this.ProviderAuth && this.ProviderAuth.userID
    },
    isFilm() {
      return this.rowType === 'film' || this.rowType === 'vimeofilm'
    },
    image() {
      const path =
        this.isFilm &&
        this.activeSlide &&
        this.activeSlide.best &&
        this.activeSlide.best.path
      return (
        path ||
        SearchUtils.getImageFromReason({
          reason: this.itemReason,
          rowType: this.rowType,
          big: true,
        })
      )
    },
    story() {
      const story = SearchUtils.getStoryFromReason({
        reason: this.itemReason,
        rowType: this.rowType,
        allPrizes: this.allPrizes,
      })
      return story
    },
    ...mapState(vxMaps.state),
    ...mapGetters(vxMaps.getters),
  },
  apollo: {
    ViLabelProgress: {
      skip() {
        // TODO needs more than this
        return !this.filmMasterID
      },
      query: ViLabelProgress,
      fetchPolicy: 'no-cache',
      variables() {
        return {
          id: this.filmMasterID,
        }
      },
    },
    MasterTypes: {
      skip() {
        return !this.filmMasterID
      },
      query: MasterTypes,
      fetchPolicy: 'cache-and-network',
      variables() {
        return {
          filters: [
            {
              valueKey: 'id',
              value: this.filmMasterID,
            },
          ],
        }
      },
    },
    ProviderAuth: {
      query: ProviderAuth,
    },
    VimeoVideoPicturesInfo: {
      skip() {
        const skip = this.hostType !== 'vimeo' || !this.hostID || !this.userID

        return skip
      },
      query: VimeoVideoPicturesInfo,
      variables() {
        return {
          id: this.userID,
          videoID: this.hostID,
          limit: 100,
        }
      },
    },
  },
}
</script>
